// @ts-nocheck
/*
 * Realtime KPI row — single row of big-number tiles (Talkdesk / Five9 /
 * Amazon Connect wallboard convention). Threshold tints turn a tile amber
 * or rose when metrics cross safe bands. Queue tile is clickable to open
 * the queue-detail popup. Trunks tile hidden for CALL_CENTRE_ADMIN.
 */
import React from 'react';
import {
  Users,
  Activity,
  PhoneCall,
  Coffee,
  PowerOff,
  ListChecks,
  Server,
  Clock,
  ChevronRight,
} from 'lucide-react';
import { USER_ROLES } from '@/utilities/constant';

type Tone = 'primary' | 'emerald' | 'blue' | 'amber' | 'rose' | 'muted';

const toneClasses: Record<Tone, { icon: string; ring: string; value: string }> = {
  primary: {
    icon: 'bg-primary/10 text-primary ring-primary/25',
    ring: 'ring-primary/20',
    value: 'text-primary',
  },
  emerald: {
    icon: 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300',
    ring: 'ring-emerald-300/40',
    value: 'text-emerald-700 dark:text-emerald-300',
  },
  blue: {
    icon: 'bg-blue-100 text-blue-700 ring-blue-200 dark:bg-blue-950/40 dark:text-blue-300',
    ring: 'ring-blue-300/40',
    value: 'text-blue-700 dark:text-blue-300',
  },
  amber: {
    icon: 'bg-amber-100 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300',
    ring: 'ring-amber-300/40',
    value: 'text-amber-700 dark:text-amber-300',
  },
  rose: {
    icon: 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300',
    ring: 'ring-rose-300/40',
    value: 'text-rose-700 dark:text-rose-300',
  },
  muted: {
    icon: 'bg-muted/60 text-muted-foreground ring-border/60',
    ring: 'ring-border/60',
    value: 'text-foreground',
  },
};

function KpiTile({
  Icon,
  label,
  value,
  helper,
  tone = 'primary',
  onClick,
}: any) {
  const tc = toneClasses[tone as Tone];
  const clickable = typeof onClick === 'function';
  return (
    <button
      type={clickable ? 'button' : undefined}
      onClick={onClick}
      className={
        'group flex min-w-0 flex-1 flex-col gap-2 rounded-xl border bg-card p-4 text-left transition-all cursor-' +
        (clickable ? 'pointer hover:-translate-y-0.5' : 'default') +
        ' border-border/60 ring-1 ring-inset ' +
        tc.ring
      }
      style={{
        boxShadow:
          '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
      }}
      disabled={!clickable}
    >
      <div className="flex items-center gap-2">
        <span
          className={
            'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset ' +
            tc.icon
          }
        >
          <Icon className="h-4 w-4" />
        </span>
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        {clickable && (
          <ChevronRight className="ml-auto h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
        )}
      </div>
      <div
        className={
          'font-bold tabular-nums leading-none text-[34px] ' + tc.value
        }
      >
        {value}
      </div>
      {helper && (
        <div className="text-[11px] font-medium text-muted-foreground">
          {helper}
        </div>
      )}
    </button>
  );
}

const Boxes = ({
  realtimeData,
  setQueuePopupVisible,
  queueDetail,
  loggedInUser,
  stateCounts,
}: any) => {
  const total = realtimeData.length;
  const online = stateCounts?.online ?? 0;
  const onCall = stateCounts?.oncall ?? 0;
  const onBreak = stateCounts?.break ?? 0;
  const offline = stateCounts?.offline ?? 0;

  const queueWaiting = Number(queueDetail?.count) || 0;
  const queueAvg = queueDetail?.average || '00:00:00';
  const queueTone: Tone =
    queueWaiting > 10 ? 'rose' : queueWaiting > 5 ? 'amber' : 'primary';

  const isCallCentreAdmin = loggedInUser === USER_ROLES.CALL_CENTRE_ADMIN;

  return (
    <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
      <KpiTile
        Icon={Users}
        label="Total Agents"
        value={total}
        helper="provisioned"
        tone="primary"
      />
      <KpiTile
        Icon={Activity}
        label="Available"
        value={online}
        helper={total ? `${Math.round((online / total) * 100)}% of total` : '—'}
        tone={online > 0 ? 'emerald' : 'muted'}
      />
      <KpiTile
        Icon={PhoneCall}
        label="On Call"
        value={onCall}
        helper={total ? `${Math.round((onCall / total) * 100)}% of total` : '—'}
        tone={onCall > 0 ? 'blue' : 'muted'}
      />
      <KpiTile
        Icon={Coffee}
        label="On Break"
        value={onBreak}
        helper={total ? `${Math.round((onBreak / total) * 100)}% of total` : '—'}
        tone={onBreak > 0 ? 'amber' : 'muted'}
      />
      <KpiTile
        Icon={PowerOff}
        label="Offline"
        value={offline}
        helper={total ? `${Math.round((offline / total) * 100)}% of total` : '—'}
        tone="muted"
      />
      <KpiTile
        Icon={ListChecks}
        label="Call Group Waiting"
        value={queueWaiting}
        helper={
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" />
            avg <span className="font-mono">{queueAvg}</span>
          </span>
        }
        tone={queueTone}
        onClick={() => setQueuePopupVisible?.(true)}
      />
      {false && (
        <div className="col-span-2 md:col-span-3 lg:col-span-6">
          <div
            className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/60 bg-card px-4 py-3"
            style={{
              boxShadow:
                '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
            }}
          >
            <div className="flex items-center gap-3">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-inset ring-primary/20">
                <Server className="h-4 w-4" />
              </span>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Trunks
                </div>
                <div className="text-xs text-muted-foreground">
                  Static totals from telephony configuration.
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-xs">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Total
                </div>
                <div className="text-base font-bold tabular-nums text-foreground">
                  2
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  On Call
                </div>
                <div className="text-base font-bold tabular-nums text-blue-700 dark:text-blue-300">
                  1
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Idle
                </div>
                <div className="text-base font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
                  1
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Offline
                </div>
                <div className="text-base font-bold tabular-nums text-muted-foreground">
                  0
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Boxes;
